import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import type {
  Application,
  ApplicationCreateInput,
  ApplicationListResponse,
  ApplicationStatusUpdateInput,
} from '../types/application'

interface ListFilters {
  demand_id?: number
  applicant_id?: number
  status?: string
}

/**
 * The council applicant list. Pass `undefined` to skip the request entirely —
 * a talent must not call this at all (#1044): the response carries applicant
 * emails, cover letters and council feedback, and the endpoint now 403s.
 *
 * A talent reading their OWN applications wants `useMyApplications`.
 */
export function useApplications(filters?: ListFilters) {
  const { showTestData } = useTestDataVisibility()
  return useQuery<ApplicationListResponse>({
    queryKey: ['applications', filters ?? null, { includeTest: showTestData }],
    // `enabled` rather than an early return: React Query still needs the hook
    // to be called unconditionally, and this keeps the skip at the call site
    // where the authorisation decision actually lives.
    enabled: filters !== undefined,
    queryFn: async () => {
      if (!filters) throw new Error('useApplications: query ran while disabled')
      const params: Record<string, string | number> = {}
      if (filters.demand_id) params.demand_id = filters.demand_id
      if (filters.applicant_id) params.applicant_id = filters.applicant_id
      if (filters.status) params.status = filters.status
      // Exclusive: include_test=true → ONLY test rows; false → ONLY real.
      params.include_test = showTestData ? 'true' : 'false'
      const res = await api.get('/applications', { params })
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useMyApplications() {
  return useQuery<ApplicationListResponse>({
    queryKey: ['applications', 'mine'],
    queryFn: async () => {
      const res = await api.get('/applications/mine')
      return res.data
    },
  })
}

export function useCreateApplication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ApplicationCreateInput) => {
      const res = await api.post('/applications', input)
      return res.data as Application
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}

export function useUpdateApplicationStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: ApplicationStatusUpdateInput }) => {
      const res = await api.patch(`/applications/${id}/status`, body)
      return res.data as Application
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] })
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['comments'] })
      // The demand itself changes shape here: `contributors` and
      // `approved_count` are derived server-side from approved applications,
      // and they feed the Staffing panel and the People list. Without this the
      // row gains its "Contributor" badge while the sidebar beside it still
      // reads "Unstaffed · 0 / 3" until a reload. `useAddTeamMember` already
      // invalidates the detail for the identical outcome.
      qc.invalidateQueries({ queryKey: ['demand'], exact: false })
    },
  })
}

export function useWithdrawApplication() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/applications/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}
